import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  IconButton,
  Typography,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Chip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import StableCKEditor from './StableCKEditor';
import ClassicEditor from '../utils/ckeditor';
import { renderRichHtml, toEditorHtml, richHtmlIsEmpty } from '../utils/sanitizeHtml';
import { tasksAPI, taskStatusesAPI, usersAPI, sprintsAPI, timeEntriesAPI, projectsAPI, customersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { TASK_TYPES, taskTypeIcon } from '../utils/taskTypes';
import TaskComments from './TaskComments';
import TaskAttachments from './TaskAttachments';
import CategoryChip from './pm/CategoryChip';
import { localISODate } from '../utils/dates';

function TaskEditDialog({ open, onClose, task, projectId, onTaskUpdated }) {
  const { isClient } = useAuth();
  const clientUser = isClient && isClient();
  const [formData, setFormData] = useState({
    name: '',
    task_id: '',
    description: '',
    project_id: '',
    customer_id: '',
    status_id: '',
    task_type: 'task',
    category: '',
    priority: 'medium',
    assignee_id: '',
    due_date: '',
    effort_estimate: '',
    sprint_id: '',
    is_timetracker_only: true,
  });

  // Description starts read-only for existing tasks; the pencil switches to the editor
  const [descEditMode, setDescEditMode] = useState(false);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');

  // Time Entry State
  const [timeEntries, setTimeEntries] = useState([]);
  const [showTimeEntryForm, setShowTimeEntryForm] = useState(false);
  const [newTimeEntry, setNewTimeEntry] = useState({
    date: localISODate(),
    hours: '',
    subtask_comment: '',
  });
  const [timeEntryError, setTimeEntryError] = useState(null);

  useEffect(() => {
    if (open) {
      loadProjects();
    }
    if (open && task) {
      loadTimeEntries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task]);

  useEffect(() => {
    // Load project-specific data when project changes, or global data if no project
    const currentProjectId = formData.project_id || projectId;
    if (open) {
      loadProjectData(currentProjectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.project_id, projectId, open]);

  useEffect(() => {
    tasksAPI.getCategories().then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (task) {
      setDescEditMode(false); // show description read-only first, edit via pencil
      setFormData({
        name: task.name || '',
        task_id: task.task_id || '',
        description: task.description || '',
        project_id: task.project_id || projectId || '',
        customer_id: task.customer_id || '',
        status_id: task.status_id || '',
        task_type: task.task_type || 'task',
        category: task.category || '',
        priority: task.priority || 'medium',
        assignee_id: task.assignee_id || '',
        due_date: task.due_date || '',
        effort_estimate: task.effort_estimate || '',
        sprint_id: task.sprint_id || '',
        is_timetracker_only: task.is_timetracker_only !== undefined ? task.is_timetracker_only : true,
      });
      // Set selected customer from task's customer_id
      if (task.customer_id) {
        setSelectedCustomerId(task.customer_id);
      }
    }
  }, [task, projectId]);

  const loadProjects = async () => {
    try {
      const [projectsRes, customersRes] = await Promise.all([
        projectsAPI.getAll(),
        customersAPI.getAll(),
      ]);
      setProjects(projectsRes.data || []);
      setCustomers(customersRes.data || []);

      // Set initial customer based on task's project
      if (task && task.project_id) {
        const taskProject = (projectsRes.data || []).find(p => p.id === task.project_id);
        if (taskProject) {
          setSelectedCustomerId(taskProject.customer_id || '');
        }
      }
    } catch (err) {
      console.error('Failed to load projects:', err);
    }
  };

  const loadProjectData = async (projId) => {
    try {
      // Always load users
      const usersRes = await usersAPI.getAll();
      setUsers(usersRes.data || []);

      // Load statuses - project-specific or global
      const statusesRes = await taskStatusesAPI.getAll(projId || null);
      const statuses = statusesRes.data || [];
      setTaskStatuses(statuses);

      // Load sprints only if project is selected
      if (projId) {
        const sprintsRes = await sprintsAPI.getAll(projId);
        setSprints(sprintsRes.data || []);
      } else {
        setSprints([]);
      }

      // Set default status "Offen" (order=0) if status_id is empty
      setFormData(prev => {
        if (!prev.status_id && statuses.length > 0) {
          const defaultStatus = statuses.find(s => s.order === 0) || statuses[0];
          return { ...prev, status_id: defaultStatus.id };
        }
        return prev;
      });
    } catch (err) {
      console.error('Failed to load project data:', err);
      setError('Failed to load project data');
    }
  };

  const loadTimeEntries = async () => {
    if (!task || !task.id) return;
    if (clientUser) return; // time tracking is internal-only

    try {
      const response = await timeEntriesAPI.getAll({ task_id: task.id });
      setTimeEntries(response.data || []);
    } catch (err) {
      console.error('Failed to load time entries:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCustomerChange = (e) => {
    const newCustomerId = e.target.value;
    setSelectedCustomerId(newCustomerId);
    // Reset project when customer changes, but set customer_id
    setFormData(prev => ({
      ...prev,
      customer_id: newCustomerId || null,
      project_id: '',
      status_id: '',
      sprint_id: '',
    }));
  };

  const handleProjectChange = (e) => {
    const newProjectId = e.target.value;
    setFormData(prev => ({
      ...prev,
      project_id: newProjectId,
      status_id: '', // Reset status when project changes
      sprint_id: '', // Reset sprint when project changes
    }));
  };

  // Filter projects by selected customer
  const filteredProjects = selectedCustomerId
    ? projects.filter(p => p.customer_id === parseInt(selectedCustomerId))
    : projects;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation: Either project_id OR customer_id must be provided
    if (!formData.project_id && !selectedCustomerId) {
      setError('Bitte wählen Sie einen Kunden oder ein Projekt aus');
      setLoading(false);
      return;
    }

    try {
      const updateData = { ...formData };
      if (!updateData.assignee_id) updateData.assignee_id = null;
      if (!updateData.due_date) updateData.due_date = null;
      if (!updateData.effort_estimate) updateData.effort_estimate = null;
      if (!updateData.sprint_id) updateData.sprint_id = null;
      if (!updateData.project_id) updateData.project_id = null;
      if (!updateData.task_id) updateData.task_id = null;

      // Set customer_id from selected customer
      updateData.customer_id = selectedCustomerId || null;

      // Clear sprint if no project (sprints are project-specific)
      // But keep status_id - it can be a global status
      if (!updateData.project_id) {
        updateData.sprint_id = null;
      }

      await tasksAPI.update(task.id, updateData);

      if (onTaskUpdated) {
        onTaskUpdated();
      }

      onClose();
    } catch (err) {
      console.error('Failed to update task:', err);
      setError(err.response?.data?.error || 'Failed to update task');
    } finally {
      setLoading(false);
    }
  };

  const handleTimeEntryChange = (e) => {
    const { name, value } = e.target;
    setNewTimeEntry(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddTimeEntry = async () => {
    setTimeEntryError(null);

    // Validation
    if (!newTimeEntry.hours || parseFloat(newTimeEntry.hours) <= 0) {
      setTimeEntryError('Bitte geben Sie eine gültige Stundenzahl ein');
      return;
    }
    if (!newTimeEntry.subtask_comment.trim()) {
      setTimeEntryError('Bitte geben Sie eine Beschreibung ein');
      return;
    }

    try {
      await timeEntriesAPI.create({
        task_id: task.id,
        date: newTimeEntry.date,
        hours: parseFloat(newTimeEntry.hours),
        subtask_comment: newTimeEntry.subtask_comment,
      });

      // Reset form
      setNewTimeEntry({
        date: localISODate(),
        hours: '',
        subtask_comment: '',
      });
      setShowTimeEntryForm(false);

      // Reload time entries
      await loadTimeEntries();
    } catch (err) {
      console.error('Failed to add time entry:', err);
      setTimeEntryError(err.response?.data?.error || 'Fehler beim Hinzufügen des Zeiteintrags');
    }
  };

  const handleDeleteTimeEntry = async (entryId) => {
    if (!window.confirm('Möchten Sie diesen Zeiteintrag wirklich löschen?')) {
      return;
    }

    try {
      await timeEntriesAPI.delete(entryId);
      await loadTimeEntries();
    } catch (err) {
      console.error('Failed to delete time entry:', err);
      setTimeEntryError(err.response?.data?.error || 'Fehler beim Löschen des Zeiteintrags');
    }
  };

  // Check if time entry is allowed for this task
  const isTimeEntryAllowed = () => {
    if (!task || !task.status) return false;
    const currentStatus = taskStatuses.find(s => s.id === task.status_id);
    if (!currentStatus) return false;
    // Allow time entries only if status is NOT Backlog (order=0) or "Zu erledigen" (order=1)
    return currentStatus.order > 1;
  };

  const getTotalHours = () => {
    return timeEntries.reduce((sum, entry) => sum + parseFloat(entry.hours || 0), 0).toFixed(2);
  };

  const handleDeactivateTask = async () => {
    if (!window.confirm('Möchten Sie diesen Task wirklich deaktivieren? Er wird nicht mehr auf dem Board angezeigt.')) {
      return;
    }

    try {
      await tasksAPI.delete(task.id);
      if (onTaskUpdated) {
        onTaskUpdated();
      }
      onClose();
    } catch (err) {
      console.error('Failed to deactivate task:', err);
      setError(err.response?.data?.error || 'Fehler beim Deaktivieren des Tasks');
    }
  };

  if (!task) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          Task bearbeiten #{task.id}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ px: 4, py: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Kunde und Projekt - Erste Zeile */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ flex: '1 1 45%', minWidth: '200px' }}>
                <InputLabel>Kunde</InputLabel>
                <Select
                  value={selectedCustomerId}
                  onChange={handleCustomerChange}
                  label="Kunde"
                >
                  <MenuItem value="">
                    <em>Alle Kunden</em>
                  </MenuItem>
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.company_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ flex: '1 1 45%', minWidth: '200px' }}>
                <InputLabel>Projekt (optional)</InputLabel>
                <Select
                  name="project_id"
                  value={formData.project_id}
                  onChange={handleProjectChange}
                  label="Projekt (optional)"
                >
                  <MenuItem value="">
                    <em>Kein Projekt (nur Zeiterfassung)</em>
                  </MenuItem>
                  {filteredProjects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Taskname und Task-ID - Zweite Zeile */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                sx={{ flex: '1 1 70%', minWidth: '300px' }}
                label="Taskname"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                autoFocus
              />
              <TextField
                sx={{ flex: '1 1 25%', minWidth: '150px' }}
                label="Task-ID (optional)"
                name="task_id"
                value={formData.task_id}
                onChange={handleChange}
                placeholder="z.B. PROJ-123"
              />
            </Box>

            {/* Kategorie-Chip sichtbar, auch wenn Details eingeklappt sind */}
            {task && task.category_info && (
              <Box sx={{ mt: -0.5 }}>
                <CategoryChip category={task.category_info} />
              </Box>
            )}

            {/* Details - immer sichtbar */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>

            {/* Task-Typ Toggle - nur für interne User */}
            {!clientUser && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_timetracker_only}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      is_timetracker_only: e.target.checked
                    }))}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTimeIcon fontSize="small" color={formData.is_timetracker_only ? 'primary' : 'disabled'} />
                    <Typography variant="body2">
                      Nur Zeiterfassung (nicht im Board anzeigen)
                    </Typography>
                  </Box>
                }
              />
            )}

            {/* Typ-Zeile */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ flex: '1 1 30%', minWidth: '200px' }}>
                <InputLabel>Typ</InputLabel>
                <Select
                  name="task_type"
                  value={formData.task_type}
                  onChange={handleChange}
                  label="Typ"
                >
                  {TASK_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {taskTypeIcon(t.value, { fontSize: 'small', sx: { color: t.color } })}
                        {t.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ flex: '1 1 60%', minWidth: '240px' }}>
                <InputLabel>Kategorie</InputLabel>
                <Select name="category" value={formData.category} onChange={handleChange} label="Kategorie">
                  <MenuItem value=""><em>Keine</em></MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.key} value={c.key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: c.color }} />
                        {c.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Zweite Zeile - Status, Priorität, Zugewiesen */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ flex: '1 1 30%', minWidth: '200px' }}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status_id"
                  value={formData.status_id}
                  onChange={handleChange}
                  label="Status"
                >
                  {taskStatuses.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      {status.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ flex: '1 1 30%', minWidth: '200px' }}>
                <InputLabel>Priorität</InputLabel>
                <Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  label="Priorität"
                >
                  <MenuItem value="low">Niedrig</MenuItem>
                  <MenuItem value="medium">Mittel</MenuItem>
                  <MenuItem value="high">Hoch</MenuItem>
                </Select>
              </FormControl>

              {clientUser ? (
                <TextField
                  sx={{ flex: '1 1 30%', minWidth: '200px' }}
                  label="Zugewiesen an"
                  value={task?.assignee ? (task.assignee.full_name || task.assignee.username) : 'Projektmanager'}
                  InputProps={{ readOnly: true }}
                  helperText="Verteilung erfolgt durch den Projektmanager"
                />
              ) : (
                <FormControl sx={{ flex: '1 1 30%', minWidth: '200px' }}>
                  <InputLabel>Zugewiesen an</InputLabel>
                  <Select
                    name="assignee_id"
                    value={formData.assignee_id}
                    onChange={handleChange}
                    label="Zugewiesen an"
                  >
                    <MenuItem value="">
                      <em>Nicht zugewiesen</em>
                    </MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.username}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>

            {/* Dritte Zeile - Datum, Aufwand, Sprint */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                sx={{ flex: '1 1 30%', minWidth: '200px' }}
                label="Fälligkeitsdatum"
                name="due_date"
                type="date"
                value={formData.due_date}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                sx={{ flex: '1 1 30%', minWidth: '200px' }}
                label="Aufwand (Punkte)"
                name="effort_estimate"
                type="number"
                value={formData.effort_estimate}
                onChange={handleChange}
                inputProps={{ min: 0 }}
              />

              <FormControl sx={{ flex: '1 1 30%', minWidth: '200px' }}>
                <InputLabel>Sprint</InputLabel>
                <Select
                  name="sprint_id"
                  value={formData.sprint_id}
                  onChange={handleChange}
                  label="Sprint"
                >
                  <MenuItem value="">
                    <em>Kein Sprint</em>
                  </MenuItem>
                  {sprints.map((sprint) => (
                    <MenuItem key={sprint.id} value={sprint.id}>
                      {sprint.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Beschreibung - Letzte Zeile, volle Breite */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Beschreibung
                </Typography>
                {!descEditMode && (
                  <IconButton
                    size="small"
                    onClick={() => setDescEditMode(true)}
                    title="Beschreibung bearbeiten"
                    aria-label="Beschreibung bearbeiten"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              {descEditMode ? (
                <Box
                  sx={{
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: 1,
                    '&:hover': {
                      borderColor: 'rgba(0, 0, 0, 0.87)',
                    },
                    '& .ck-editor__editable': {
                      minHeight: '300px',
                    },
                  }}
                >
                  <StableCKEditor
                    editor={ClassicEditor}
                    // Normalized so plain-text descriptions (integrations API,
                    // mail import) keep their paragraphs and lists in the editor.
                    data={toEditorHtml(formData.description || '')}
                    config={{
                      placeholder: 'Detaillierte Beschreibung der Aufgabe...',
                      toolbar: [
                        'heading', '|',
                        'bold', 'italic', 'link', '|',
                        'bulletedList', 'numberedList', '|',
                        'blockQuote', 'insertTable', '|',
                        'undo', 'redo'
                      ],
                    }}
                    onChange={(_, editor) => {
                      const data = editor.getData();
                      setFormData(prev => ({
                        ...prev,
                        description: data,
                      }));
                    }}
                  />
                </Box>
              ) : richHtmlIsEmpty(formData.description) ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: 'italic', cursor: 'pointer' }}
                  onClick={() => setDescEditMode(true)}
                >
                  Keine Beschreibung – zum Hinzufügen auf das Stift-Symbol klicken.
                </Typography>
              ) : (
                <Box
                  onClick={() => setDescEditMode(true)}
                  sx={{
                    cursor: 'text',
                    px: 1.5, py: 1,
                    borderRadius: 1,
                    border: '1px solid transparent',
                    '&:hover': { borderColor: 'rgba(0, 0, 0, 0.12)' },
                    '& p': { my: 0.5 },
                    '& table': { borderCollapse: 'collapse' },
                    '& td, & th': { border: '1px solid rgba(0,0,0,0.23)', px: 1, py: 0.5 },
                    '& a': { color: 'primary.main' },
                  }}
                  dangerouslySetInnerHTML={{ __html: renderRichHtml(formData.description) }}
                />
              )}
            </Box>

              </Box>

            {/* Anhänge (Screenshots/Logs zu Bugs) - intern UND Kunde */}
            {task && task.id && <TaskAttachments taskId={task.id} />}

            {/* Comments - für interne UND externe (Kunden) User */}
            {task && task.id && <TaskComments taskId={task.id} />}

            {/* Time Entries Section - nur für interne User */}
            {!clientUser && <>
            <Divider sx={{ my: 2 }} />

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTimeIcon color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Zeiterfassung
                  </Typography>
                  {timeEntries.length > 0 && (
                    <Chip
                      label={`${getTotalHours()} Stunden`}
                      color="primary"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>
                {isTimeEntryAllowed() && !showTimeEntryForm && (
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    size="small"
                    onClick={() => setShowTimeEntryForm(true)}
                  >
                    Zeit erfassen
                  </Button>
                )}
              </Box>

              {/* Info for non-allowed statuses */}
              {!isTimeEntryAllowed() && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Zeiterfassung ist nur für Tasks im Status "In Bearbeitung", "Review" oder "Fertig" möglich.
                </Alert>
              )}

              {/* Time Entry Form */}
              {showTimeEntryForm && isTimeEntryAllowed() && (
                <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Neuer Zeiteintrag
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <TextField
                      sx={{ flex: '1 1 200px' }}
                      label="Datum"
                      name="date"
                      type="date"
                      value={newTimeEntry.date}
                      onChange={handleTimeEntryChange}
                      InputLabelProps={{ shrink: true }}
                      required
                      size="small"
                    />
                    <TextField
                      sx={{ flex: '1 1 150px' }}
                      label="Stunden"
                      name="hours"
                      type="number"
                      value={newTimeEntry.hours}
                      onChange={handleTimeEntryChange}
                      inputProps={{ min: 0, step: 0.25 }}
                      required
                      size="small"
                    />
                  </Box>
                  <TextField
                    fullWidth
                    label="Beschreibung"
                    name="subtask_comment"
                    value={newTimeEntry.subtask_comment}
                    onChange={handleTimeEntryChange}
                    multiline
                    rows={2}
                    required
                    size="small"
                    placeholder="Was wurde in dieser Zeit gemacht?"
                    sx={{ mb: 2 }}
                  />
                  {timeEntryError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {timeEntryError}
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      onClick={() => {
                        setShowTimeEntryForm(false);
                        setTimeEntryError(null);
                      }}
                    >
                      Abbrechen
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleAddTimeEntry}
                    >
                      Hinzufügen
                    </Button>
                  </Box>
                </Paper>
              )}

              {/* Time Entries Table */}
              {timeEntries.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Datum</strong></TableCell>
                        <TableCell><strong>Stunden</strong></TableCell>
                        <TableCell><strong>Beschreibung</strong></TableCell>
                        <TableCell><strong>Benutzer</strong></TableCell>
                        <TableCell align="right"><strong>Aktionen</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {timeEntries.map((entry) => (
                        <TableRow key={entry.id} hover>
                          <TableCell>
                            {new Date(entry.date).toLocaleDateString('de-DE')}
                          </TableCell>
                          <TableCell>{entry.hours}h</TableCell>
                          <TableCell sx={{ maxWidth: 300 }}>
                            {entry.subtask_comment}
                          </TableCell>
                          <TableCell>{entry.username}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteTimeEntry(entry.id)}
                              title="Löschen"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                !showTimeEntryForm && (
                  <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Typography variant="body2" color="text.secondary">
                      Noch keine Zeiteinträge vorhanden
                    </Typography>
                  </Paper>
                )
              )}
            </Box>
            </>}
          </Box>

          {error && (
            <Box mt={2} color="error.main">
              {error}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Button
            onClick={handleDeactivateTask}
            startIcon={<DeleteIcon />}
            color="error"
            variant="outlined"
          >
            Task deaktivieren
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose}>Abbrechen</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Speichern...' : 'Änderungen speichern'}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default TaskEditDialog;
